import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, forkJoin, of, firstValueFrom } from 'rxjs';
import { skip, take } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { toLocalDateTimeInputValue, toLocalISOString } from '../../utils/date-utils';
import {
  ControlMentorService,
  MentorOption,
  MentoringDashboard,
  MentoringEnrollment,
  MentoringEnrollmentStatus,
  MentoringMentoradoBusiness,
  MentoringMentoradoProfile,
  MentoringProgram,
  MentoringProgramStatus,
  MentoringSchedule,
  MentoringScheduleStatus,
  MentoringSessionTask,
  MentoringTaskStatus,
  MentoringSessionFile
} from '../../services/controlmentor.service';
import { UserSessionService } from '../../services/user-session.service';

  @Component({
  selector: 'app-controlmentor',
  templateUrl: './controlmentor.component.html',
  styleUrls: ['./controlmentor.component.scss']
})
export class ControlMentorComponent implements OnInit, OnDestroy {
  tenantId: number | null = null;
  userId: number | null = null;

  dashboard: MentoringDashboard | null = null;
  programs: MentoringProgram[] = [];
  enrollments: MentoringEnrollment[] = [];
  filteredEnrollments: MentoringEnrollment[] = [];
  schedules: MentoringSchedule[] = [];
  history: any[] = [];
  mentorOptions: MentorOption[] = [];

  selectedProgram: MentoringProgram | null = null;
  selectedEnrollment: MentoringEnrollment | null = null;
  previousSession: MentoringSchedule | null = null;
  upcomingSession: MentoringSchedule | null = null;
  currentSession: MentoringSchedule | null = null;
  previousSessionTasks: MentoringSessionTask[] = [];
  upcomingSessionTasks: MentoringSessionTask[] = [];
  currentSessionTasks: MentoringSessionTask[] = [];
  previousSessionFiles: MentoringSessionFile[] = [];
  currentSessionFiles: MentoringSessionFile[] = [];
  mentoradoCpfFilter = '';
  showAllEnrollments = true;

  programStatusOptions: MentoringProgramStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];
  deliveryTypeOptions = [
    { value: 'ONE_ON_ONE', label: 'One-on-one' },
    { value: 'CLASS', label: 'Turmas' },
    { value: 'MIXED', label: 'Híbrido' }
  ];
  formatOptions = [
    { value: 'WHITEBOARD', label: 'Whiteboard' },
    { value: 'VIDEO', label: 'Vídeo' },
    { value: 'MATERIAL', label: 'Materiais' }
  ];
  enrollmentStatusOptions: { value: MentoringEnrollmentStatus; label: string }[] = [
    { value: 'IN_PROGRESS', label: 'Em andamento' },
    { value: 'COMPLETED', label: 'Concluída' },
    { value: 'CANCELLED', label: 'Cancelada' }
  ];
  businessSectorOptions: string[] = [
    'Agronegócio',
    'Alimentício',
    'Automotivo',
    'Construção civil',
    'Educação',
    'Energia e utilities',
    'Entretenimento e mídia',
    'Financeiro e bancário',
    'Farmacêutico e saúde',
    'Indústria química',
    'Logística e transporte',
    'Manufatura',
    'Mercado imobiliário',
    'Serviços profissionais',
    'Tecnologia da informação',
    'Telecomunicações',
    'Turismo e hotelaria',
    'Varejo e e-commerce'
  ];
  educationLevelOptions: string[] = [
    'Ensino fundamental incompleto',
    'Ensino fundamental completo',
    'Ensino médio incompleto',
    'Ensino médio completo',
    'Curso técnico',
    'Graduação incompleta',
    'Graduação completa',
    'Pós-graduação / MBA',
    'Mestrado',
    'Doutorado'
  ];
  jobRoleOptions: string[] = [
    'Estagiário',
    'Trainee',
    'Assistente',
    'Analista Júnior',
    'Analista Pleno',
    'Analista Sênior',
    'Coordenador',
    'Supervisor',
    'Especialista',
    'Líder de equipe',
    'Gerente',
    'Gerente Sênior',
    'Diretor / Head',
    'Vice-presidente',
    'C-level',
    'Consultor'
  ];
  private readonly shortDateTimeOptions: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'short' };
  private readonly mediumDateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };

  programForm: FormGroup;
  enrollmentForm: FormGroup;
  profileForm: FormGroup;
  businessForm: FormGroup;
  scheduleForm: FormGroup;
  agendaTaskForm: FormGroup;
  agendaNoteForm: FormGroup;
  programEditForm: FormGroup;
  sessionNoteForm: FormGroup;
  currentTaskForm: FormGroup;
  sessionRescheduleForm: FormGroup;
  agendaModalOpen = false;
  activeAgendaTab: 'overview' | 'history' | 'details' | 'workplan' = 'overview';
  agendaAttachment: File | null = null;
  detailsEditMode = false;
  programEditModalOpen = false;
  editingProgram: MentoringProgram | null = null;
  courseTags: string[] = [];
  keywordTags: string[] = [];
  painsTags: string[] = [];
  responsibilityTags: string[] = [];
  openingSession = false;
  editingUpcomingDate = false;
  currentSessionView: 'preview' | 'active' | 'reschedule' = 'preview';
  activeSessionId: number | null = null;
  taskPanelCollapsed = false;
  sessionAttachmentLoading = false;
  savingWorkPlan = false;
  workPlanEditMode = false;
  private workPlanDraftBackup = '';
  @ViewChild('workPlanEditor') workPlanEditor?: ElementRef<HTMLDivElement>;
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;
  editingTaskId: number | null = null;
  editingTaskTitle = '';
  editingTaskNotes = '';
  deletingSessionsId: number | null = null;
  private readonly pdfMargin = 48;
  private readonly pdfBrandPrimary = { r: 26, g: 188, b: 172 };
  private readonly pdfBrandDark = { r: 24, g: 39, b: 66 };
  private readonly pdfTextColor = { r: 41, g: 47, b: 54 };
  private readonly pdfMutedColor = { r: 107, g: 114, b: 128 };
  private readonly pdfChipColor = { r: 229, g: 244, b: 241 };
  private pdfLogoDataUrl: string | null = null;
  private readonly pdfLogoAssetPath = 'assets/MIV/brand/versao 1.png';

  private subscriptions: Subscription[] = [];
  private previousTasksSub?: Subscription;
  private previousFilesSub?: Subscription;
  private upcomingTasksSub?: Subscription;
  private currentTasksSub?: Subscription;
  private currentFilesSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private mentorService: ControlMentorService,
    private session: UserSessionService
  ) {
    this.programForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      website: [''],
      delivery_type: ['ONE_ON_ONE', Validators.required],
      format: ['WHITEBOARD', Validators.required],
      status: ['DRAFT', Validators.required]
    });

    this.enrollmentForm = this.fb.group({
      mentoring_program_id: ['', Validators.required],
      mentor_id: ['', Validators.required],
      mentorado_name: ['', Validators.required],
      mentorado_email: ['', [Validators.required, Validators.email]],
      mentorado_cpf: ['', Validators.required],
      session_count: [0, [Validators.min(0)]]
    });

    this.profileForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      cpf: ['', Validators.required],
      phone: [''],
      education_level: [''],
      courses_taken: [''],
      profile_description: [''],
      professional_experience: [''],
      extra_experience: [''],
      objective: [''],
      objective_keywords: [''],
      pains_description: [''],
      pains_keywords: [''],
      work_plan: ['']
    });

    this.businessForm = this.fb.group({
      business_sector: [''],
      company_website: [''],
      business_description: [''],
      job_title: [''],
      role_description: [''],
      responsibilities: [''],
      daily_activities: ['']
    });

    this.scheduleForm = this.fb.group({
      meeting_date: ['', Validators.required]
    });

    this.agendaTaskForm = this.fb.group({
      title: ['', Validators.required],
      notes: ['']
    });

    this.agendaNoteForm = this.fb.group({
      note: ['', Validators.required]
    });

    this.programEditForm = this.fb.group({
      name: ['', Validators.required],
      website: [''],
      format: ['WHITEBOARD', Validators.required],
      delivery_type: ['ONE_ON_ONE', Validators.required],
      status: ['DRAFT', Validators.required],
      description: ['']
    });

    this.sessionNoteForm = this.fb.group({
      note: ['', Validators.required]
    });

    this.currentTaskForm = this.fb.group({
      title: ['', Validators.required],
      notes: ['']
    });

    this.sessionRescheduleForm = this.fb.group({
      meeting_date: ['', Validators.required]
    });
  }

  ngOnInit() {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    if (!this.tenantId) {
      return;
    }

    this.subscriptions.push(
      this.mentorService.listPrograms(this.tenantId).subscribe((programs) => {
        this.programs = programs;
        if (!this.selectedProgram && programs.length) {
          this.selectedProgram = programs[0];
          this.enrollmentForm.patchValue({ mentoring_program_id: programs[0].id });
          this.syncEnrollmentFilter();
        } else {
          this.syncEnrollmentFilter();
        }
      })
    );

    this.subscriptions.push(
      this.mentorService.listEnrollments(this.tenantId).subscribe((enrollments) => {
        this.enrollments = enrollments;
        this.syncEnrollmentFilter();
      })
    );

    this.subscriptions.push(
      this.mentorService.listSchedules(this.tenantId).subscribe((schedules) => {
        this.schedules = schedules;
        this.resolveSessionsContext();
      })
    );

    this.subscriptions.push(
      this.mentorService.listHistory(this.tenantId).subscribe((history) => {
        this.history = history;
      })
    );

    this.subscriptions.push(
      this.mentorService.getDashboardSummary(this.tenantId).subscribe((summary) => {
        this.dashboard = summary;
      })
    );

    this.subscriptions.push(
      this.mentorService.listMentors(this.tenantId).subscribe((mentors) => {
        this.mentorOptions = mentors;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.previousTasksSub?.unsubscribe();
    this.upcomingTasksSub?.unsubscribe();
    this.currentTasksSub?.unsubscribe();
    this.currentFilesSub?.unsubscribe();
  }

  selectProgram(program: MentoringProgram) {
    this.selectedProgram = program;
    this.showAllEnrollments = false;
    this.enrollmentForm.patchValue({ mentoring_program_id: program.id });
    this.syncEnrollmentFilter();
  }

  openProgramEdit(program: MentoringProgram) {
    this.selectProgram(program);
    this.editingProgram = program;
    this.programEditForm.patchValue({
      name: program.name,
      website: program.website || '',
      format: program.format,
      delivery_type: program.delivery_type,
      status: program.status,
      description: program.description || ''
    });
    this.programEditModalOpen = true;
  }

  closeProgramEdit() {
    this.programEditModalOpen = false;
    this.editingProgram = null;
  }

  submitProgramEdit() {
    if (!this.programEditForm.valid || !this.editingProgram) {
      this.programEditForm.markAllAsTouched();
      return;
    }
    this.mentorService.updateProgram(this.editingProgram.id, this.programEditForm.value);
    this.closeProgramEdit();
  }

  onCpfFilterChange(value: string) {
    this.mentoradoCpfFilter = value;
    this.syncEnrollmentFilter();
  }

  setEnrollmentScope(scope: 'all' | 'program') {
    this.showAllEnrollments = scope === 'all';
    this.syncEnrollmentFilter();
  }

  programNameFor(enrollment: MentoringEnrollment) {
    return this.programs.find((program) => program.id === enrollment.mentoring_program_id)?.name || 'Programa';
  }

  createProgram() {
    if (!this.programForm.valid || !this.tenantId || !this.userId) {
      this.programForm.markAllAsTouched();
      return;
    }
    this.mentorService.createProgram(this.tenantId, this.userId, this.programForm.value).pipe(take(1)).subscribe(() => {
      this.programForm.reset({
        name: '',
        description: '',
        website: '',
        delivery_type: 'ONE_ON_ONE',
        format: 'WHITEBOARD',
        status: 'DRAFT'
      });
    });
  }

  createEnrollment() {
    if (!this.enrollmentForm.valid || !this.tenantId || !this.userId) {
      this.enrollmentForm.markAllAsTouched();
      return;
    }
    this.mentorService.createEnrollment(this.tenantId, this.userId, this.enrollmentForm.value).pipe(take(1)).subscribe(() => {
      this.enrollmentForm.reset({
        mentoring_program_id: this.selectedProgram?.id || '',
        mentor_id: '',
        mentorado_name: '',
        mentorado_email: '',
        mentorado_cpf: ''
      });
    });
  }

  selectEnrollment(enrollment: MentoringEnrollment) {
    this.selectedEnrollment = enrollment;
    this.loadMentoradoForms(enrollment.id);
    this.resolveSessionsContext();
  }

  changeEnrollmentStatus(enrollment: MentoringEnrollment, status: MentoringEnrollmentStatus | string) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const normalized = (status as MentoringEnrollmentStatus);
    this.mentorService.updateEnrollmentStatus(enrollment.id, normalized, this.tenantId, this.userId);
  }

  confirmSessionsDeletion(enrollment: MentoringEnrollment, event: Event) {
    event.stopPropagation();
    if (!this.tenantId || this.deletingSessionsId === enrollment.id) {
      return;
    }
    const message = `Deseja excluir todas as sessões, tarefas e anexos do mentorado ${enrollment.mentorado_snapshot.name}? Esta ação não pode ser desfeita.`;
    if (!confirm(message)) {
      return;
    }
    this.deletingSessionsId = enrollment.id;
    this.mentorService
      .deleteAllSessions(enrollment.id, this.tenantId, this.userId || undefined)
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (this.selectedEnrollment?.id === enrollment.id) {
            this.resolveSessionsContext();
          }
          this.deletingSessionsId = null;
        },
        error: () => {
          this.deletingSessionsId = null;
        }
      });
  }

  saveProfile(options?: { logWorkPlan?: boolean }): Observable<MentoringMentoradoProfile | null> {
    if (!this.selectedEnrollment) {
      return of(null);
    }
    this.profileForm.patchValue(
      {
        courses_taken: this.tagsToString(this.courseTags),
        objective_keywords: this.tagsToString(this.keywordTags),
        pains_keywords: this.tagsToString(this.painsTags)
      },
      { emitEvent: false }
    );
    const payload = {
      ...this.profileForm.value,
      cpf: this.stripNonDigits(this.profileForm.value.cpf),
      phone: this.stripNonDigits(this.profileForm.value.phone)
    };
    return this.mentorService.saveMentoradoProfile(this.selectedEnrollment.id, payload, {
      userId: this.userId || undefined,
      logWorkPlan: options?.logWorkPlan,
      tenantId: this.tenantId || undefined
    });
  }

  saveBusiness() {
    if (!this.selectedEnrollment) {
      return of(null);
    }
    this.businessForm.patchValue(
      {
        responsibilities: this.tagsToString(this.responsibilityTags)
      },
      { emitEvent: false }
    );
    return this.mentorService.saveMentoradoBusiness(this.selectedEnrollment.id, this.businessForm.value);
  }

  scheduleSession() {
    if (!this.scheduleForm.valid || !this.selectedEnrollment || !this.tenantId || !this.userId) {
      this.scheduleForm.markAllAsTouched();
      return;
    }
    const value = this.scheduleForm.value;
    const meetingISO = toLocalISOString(new Date(value.meeting_date));

    if (this.upcomingSession && this.editingUpcomingDate) {
      this.mentorService.updateSchedule(
        this.upcomingSession.id,
        {
          meeting_date: meetingISO
        },
        { userId: this.userId || undefined, tenantId: this.tenantId || undefined }
      );
      this.editingUpcomingDate = false;
    } else if (!this.upcomingSession) {
      this.mentorService.scheduleSession(
        this.tenantId,
        {
          mentoring_enrollment_id: this.selectedEnrollment.id,
          mentorado_name: this.selectedEnrollment.mentorado_snapshot.name,
          mentorado_cpf: this.selectedEnrollment.mentorado_snapshot.cpf,
          meeting_date: meetingISO,
          controltask_task_id: null
        },
        this.userId
      );
    }

    this.scheduleForm.reset({ meeting_date: '' });
  }

  openCurrentSession() {
    if (!this.currentSession) {
      return;
    }
    this.activeSessionId = this.currentSession.id;
    this.currentSessionView = 'active';
    this.taskPanelCollapsed = false;
  }

  enterRescheduleMode() {
    if (!this.currentSession) {
      return;
    }
    this.currentSessionView = 'reschedule';
    this.sessionRescheduleForm.patchValue({
      meeting_date: this.toInputDateValue(this.currentSession.meeting_date)
    });
  }

  cancelRescheduleMode() {
    if (!this.currentSession) {
      this.currentSessionView = 'preview';
      return;
    }
    this.currentSessionView = this.activeSessionId === this.currentSession.id ? 'active' : 'preview';
    this.sessionRescheduleForm.reset({ meeting_date: '' });
    this.sessionAttachmentLoading = false;
  }

  rescheduleCurrentSession() {
    if (!this.currentSession) {
      return;
    }
    if (!this.sessionRescheduleForm.valid) {
      this.sessionRescheduleForm.markAllAsTouched();
      return;
    }
    const meetingISO = toLocalISOString(new Date(this.sessionRescheduleForm.value.meeting_date));
    this.mentorService.updateSchedule(
      this.currentSession.id,
      {
        meeting_date: meetingISO
      },
      { userId: this.userId || undefined, tenantId: this.tenantId || undefined }
    );
    this.activeSessionId = null;
    this.currentSessionView = 'preview';
    this.sessionRescheduleForm.reset({ meeting_date: '' });
  }

  toggleTaskPanel() {
    this.taskPanelCollapsed = !this.taskPanelCollapsed;
  }

  handleSessionFile(event: Event) {
    if (!this.currentSession) {
      return;
    }
    const input = event.target as HTMLInputElement | null;
    const files = input?.files;
    if (!files || !files.length) {
      return;
    }
    this.sessionAttachmentLoading = true;
    let remaining = files.length;
    Array.from(files).forEach((file) => {
      const sub = this.mentorService.uploadSessionFile(this.currentSession!.id, file);
      sub.add(() => {
        remaining -= 1;
        if (remaining === 0) {
          this.sessionAttachmentLoading = false;
          if (input) {
            input.value = '';
          }
        }
      });
    });
  }

  updateSessionStatus(session: MentoringSchedule, status: 'SCHEDULED' | 'DONE' | 'CANCELLED') {
    this.mentorService.updateSessionStatus(session.id, status, {
      userId: this.userId || undefined,
      tenantId: this.tenantId || undefined
    });
  }

  statusChipClass(status: MentoringEnrollmentStatus) {
    return {
      progress: status === 'IN_PROGRESS',
      completed: status === 'COMPLETED',
      cancelled: status === 'CANCELLED'
    };
  }

  translateEnrollmentStatus(status: MentoringEnrollmentStatus) {
    const match = this.enrollmentStatusOptions.find((option) => option.value === status);
    return match?.label || status;
  }

  programStatusClass(status: MentoringProgramStatus) {
    return {
      active: status === 'ACTIVE',
      draft: status === 'DRAFT',
      inactive: status === 'INACTIVE',
      archived: status === 'ARCHIVED'
    };
  }

  scheduleForActiveEnrollment() {
    if (!this.selectedEnrollment) {
      return [];
    }
    return this.schedules.filter((row) => row.mentoring_enrollment_id === this.selectedEnrollment!.id);
  }

  timelineForActiveEnrollment() {
    if (!this.selectedEnrollment) {
      return [];
    }
    return this.history.filter((row) => row.mentoring_enrollment_id === this.selectedEnrollment!.id);
  }

  mentorNameFor(enrollment: MentoringEnrollment): string {
    const mentor = this.mentorOptions.find((option) => option.id === enrollment.mentor_id);
    return mentor?.name || '—';
  }

  formatDate(date: string | null | undefined, options?: Intl.DateTimeFormatOptions) {
    if (!date) {
      return '—';
    }
    return new Intl.DateTimeFormat('pt-BR', options || this.shortDateTimeOptions).format(new Date(date));
  }

  formatScheduleDate(date: string | null | undefined) {
    return this.formatDate(date, this.mediumDateOptions);
  }

  formatTimelineDate(date: string | null | undefined) {
    return this.formatDate(date, this.shortDateTimeOptions);
  }

  isTodaySession(session: MentoringSchedule | null) {
    if (!session) {
      return false;
    }
    const target = new Date(session.meeting_date);
    const now = new Date();
    return target.toDateString() === now.toDateString();
  }

  completedTaskCount(tasks: MentoringSessionTask[]) {
    return tasks.filter((task) => task.status === 'DONE').length;
  }

  taskCompletionPercent(tasks: MentoringSessionTask[]) {
    if (!tasks.length) {
      return 0;
    }
    return Math.round((this.completedTaskCount(tasks) / tasks.length) * 100);
  }

  toggleTaskProgress(task: MentoringSessionTask) {
    const nextStatus: MentoringTaskStatus = task.status === 'DONE' ? 'PENDING' : 'DONE';
    this.mentorService.updateSessionTaskStatus(task.id, nextStatus);
  }

  startTaskEdition(task: MentoringSessionTask, event?: Event) {
    event?.stopPropagation();
    this.editingTaskId = task.id;
    this.editingTaskTitle = task.title;
    this.editingTaskNotes = task.notes || '';
  }

  cancelTaskEdition() {
    this.editingTaskId = null;
    this.editingTaskTitle = '';
    this.editingTaskNotes = '';
  }

  saveTaskEdition() {
    if (!this.editingTaskId) {
      return;
    }
    const title = this.editingTaskTitle.trim();
    if (!title) {
      return;
    }
    this.mentorService.updateSessionTask(this.editingTaskId, {
      title,
      notes: this.editingTaskNotes.trim() || ''
    });
    this.cancelTaskEdition();
  }

  deleteTask(task: MentoringSessionTask, event?: Event) {
    event?.stopPropagation();
    if (this.editingTaskId === task.id) {
      this.cancelTaskEdition();
    }
    this.mentorService.deleteSessionTask(task.id);
  }

  reorderUpcomingTasks(event: CdkDragDrop<MentoringSessionTask[]>) {
    if (!event.container?.data || event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    if (this.upcomingSession) {
      const orderedIds = event.container.data.map((task) => task.id);
      this.mentorService.reorderSessionTasks(this.upcomingSession.id, orderedIds);
    }
  }

  triggerAttachmentInput() {
    this.attachmentInput?.nativeElement?.click();
  }

  registerNextTask() {
    if (!this.agendaTaskForm.valid || !this.upcomingSession) {
      this.agendaTaskForm.markAllAsTouched();
      return;
    }
    const value = this.agendaTaskForm.value;
    const title = (value.title || '').trim();
    if (!title) {
      this.agendaTaskForm.get('title')?.setErrors({ required: true });
      return;
    }
    this.mentorService.addTaskToSession(this.upcomingSession.id, {
      title,
      notes: value.notes?.trim() || '',
      attachment: this.agendaAttachment
    });
    this.agendaTaskForm.reset({ title: '', notes: '' });
    this.agendaAttachment = null;
  }

  saveAgendaNote() {
    if (!this.agendaNoteForm.valid || !this.selectedEnrollment || !this.tenantId || !this.userId) {
      this.agendaNoteForm.markAllAsTouched();
      return;
    }
    const note = (this.agendaNoteForm.value.note || '').trim();
    if (!note) {
      this.agendaNoteForm.get('note')?.setErrors({ required: true });
      return;
    }
    this.mentorService.addAgendaNote(this.selectedEnrollment.id, this.tenantId, this.userId, note);
    this.agendaNoteForm.reset({ note: '' });
  }

  hasSessions() {
    return this.scheduleForActiveEnrollment().length > 0;
  }

  sessionHistory(): MentoringSchedule[] {
    return this.scheduleForActiveEnrollment()
      .slice()
      .sort((a, b) => new Date(b.meeting_date).getTime() - new Date(a.meeting_date).getTime());
  }

  sessionNumber(session: MentoringSchedule | null) {
    if (!session) {
      return 0;
    }
    const sessions = this.scheduleForActiveEnrollment()
      .slice()
      .sort((a, b) => new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime());
    const index = sessions.findIndex((row) => row.id === session.id);
    return index >= 0 ? index + 1 : sessions.length;
  }

  sessionStatusLabel(status: MentoringScheduleStatus | string | null | undefined) {
    switch (status) {
      case 'DONE':
        return 'Concluída';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return 'Em andamento';
    }
  }

  translateHistoryAction(action?: string | null) {
    switch (action) {
      case 'MEETING_SCHEDULED':
        return 'Sessão agendada';
      case 'SESSION_RESCHEDULED':
        return 'Sessão reagendada';
      case 'SESSION_COMPLETED':
        return 'Sessão concluída';
      case 'STATUS_CHANGE':
        return 'Status atualizado';
      case 'NOTE_ADDED':
        return 'Nota adicionada';
      case 'WORK_PLAN_UPDATED':
        return 'Plano de trabalho atualizado';
      case 'MENTOR_REGISTERED':
        return 'Mentorado cadastrado';
      default:
        return action || 'Atualização';
    }
  }

  notesForSession(session: MentoringSchedule | null): string[] {
    if (!session) {
      return [];
    }
    const prefix = `Sessão ${this.sessionNumber(session)}:`;
    return this.timelineForActiveEnrollment()
      .filter((item) => item.description?.startsWith(prefix))
      .map((item) => item.description?.replace(prefix, '').trim() || '')
      .filter((text) => !!text);
  }

  previousSessionProgress() {
    if (this.previousSessionTasks.length) {
      return this.taskCompletionPercent(this.previousSessionTasks);
    }
    return this.selectedEnrollment?.progress_percent || 0;
  }

  registerCurrentTask() {
    if (!this.currentTaskForm.valid || !this.currentSession) {
      this.currentTaskForm.markAllAsTouched();
      return;
    }
    const value = this.currentTaskForm.value;
    const title = (value.title || '').trim();
    if (!title) {
      this.currentTaskForm.get('title')?.setErrors({ required: true });
      return;
    }
    this.mentorService.addTaskToSession(this.currentSession.id, {
      title,
      notes: value.notes?.trim() || ''
    });
    this.currentTaskForm.reset({ title: '', notes: '' });
  }

  saveSessionObservation() {
    if (!this.sessionNoteForm.valid || !this.currentSession || !this.tenantId || !this.userId) {
      this.sessionNoteForm.markAllAsTouched();
      return;
    }
    const note = (this.sessionNoteForm.value.note || '').trim();
    if (!note) {
      this.sessionNoteForm.get('note')?.setErrors({ required: true });
      return;
    }
    const sessionIndex = this.sessionNumber(this.currentSession);
    this.mentorService.addAgendaNote(
      this.currentSession.mentoring_enrollment_id,
      this.tenantId,
      this.userId,
      `Sessão ${sessionIndex}: ${note}`
    );
    this.sessionNoteForm.reset({ note: '' });
  }

  canScheduleNextSession() {
    return !this.currentSession || this.currentSessionView === 'active';
  }

  openNewSession() {
    if (!this.selectedEnrollment || !this.tenantId || !this.userId || this.openingSession) {
      return;
    }
    this.openingSession = true;
    const sub = this.mentorService.scheduleSession(
      this.tenantId,
      {
        mentoring_enrollment_id: this.selectedEnrollment.id,
        mentorado_name: this.selectedEnrollment.mentorado_snapshot.name,
        mentorado_cpf: this.selectedEnrollment.mentorado_snapshot.cpf,
        meeting_date: toLocalISOString(new Date()),
        controltask_task_id: null
      },
      this.userId
    );
    sub.add(() => (this.openingSession = false));
  }

  concludeCurrentSession() {
    if (!this.currentSession) {
      return;
    }
    this.mentorService.updateSessionStatus(this.currentSession.id, 'DONE');
    this.activeSessionId = null;
    this.currentSessionView = 'preview';
  }

  reopenSession(session: MentoringSchedule | null) {
    if (!session) {
      return;
    }
    this.mentorService.updateSessionStatus(session.id, 'SCHEDULED');
  }

  openAgendaModal() {
    this.agendaModalOpen = true;
    this.activeAgendaTab = 'overview';
    this.agendaTaskForm.reset({ title: '', notes: '' });
    this.agendaNoteForm.reset({ note: '' });
    this.sessionNoteForm.reset({ note: '' });
    this.currentTaskForm.reset({ title: '', notes: '' });
    this.workPlanEditMode = false;
    this.savingWorkPlan = false;
    this.agendaAttachment = null;
  }

  closeAgendaModal() {
    this.agendaModalOpen = false;
  }

  openProfileModal(enrollment: MentoringEnrollment) {
    this.selectEnrollment(enrollment);
    this.openAgendaModal();
  }

  switchAgendaTab(tab: 'overview' | 'history' | 'details' | 'workplan') {
    this.activeAgendaTab = tab;
  }

  handleAgendaAttachment(event: Event) {
    const input = event.target as HTMLInputElement | null;
    this.agendaAttachment = input?.files?.[0] || null;
  }

  editUpcomingSession() {
    if (!this.upcomingSession) {
      return;
    }
    this.editingUpcomingDate = true;
    this.scheduleForm.patchValue({
      meeting_date: this.toInputDateValue(this.upcomingSession.meeting_date)
    });
  }

  cancelUpcomingEdit() {
    this.editingUpcomingDate = false;
    this.scheduleForm.reset({ meeting_date: '' });
  }

  enterDetailsEdit() {
    this.detailsEditMode = true;
  }

  cancelDetailsEdit() {
    this.detailsEditMode = false;
    if (this.selectedEnrollment) {
      this.loadMentoradoForms(this.selectedEnrollment.id);
    }
  }

  saveDetailsForms() {
    if (!this.selectedEnrollment) {
      return;
    }
    const profile$ = this.saveProfile();
    const business$ = this.saveBusiness();
    forkJoin([profile$, business$])
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (this.selectedEnrollment) {
            this.loadMentoradoForms(this.selectedEnrollment.id);
          }
          this.detailsEditMode = false;
        },
        error: () => {
          if (this.selectedEnrollment) {
            this.loadMentoradoForms(this.selectedEnrollment.id);
          }
        }
      });
  }

  addTagFromInput(event: Event, type: 'courses' | 'keywords' | 'responsibilities' | 'pains') {
    event.preventDefault();
    const input = event.target as HTMLInputElement | null;
    const value = input?.value.trim();
    if (!value) {
      if (input) {
        input.value = '';
      }
      return;
    }
    const list = this.getTagList(type);
    const exists = list.some((tag) => tag.toLowerCase() === value.toLowerCase());
    if (!exists) {
      list.push(value);
      this.syncTagControl(type);
    }
    if (input) {
      input.value = '';
    }
  }

  removeTag(type: 'courses' | 'keywords' | 'responsibilities' | 'pains', index: number) {
    const list = this.getTagList(type);
    list.splice(index, 1);
    this.syncTagControl(type);
  }

  trackById(_index: number, item: { id: number }) {
    return item.id;
  }

  private getTagList(type: 'courses' | 'keywords' | 'responsibilities' | 'pains') {
    if (type === 'courses') {
      return this.courseTags;
    }
    if (type === 'keywords') {
      return this.keywordTags;
    }
    if (type === 'pains') {
      return this.painsTags;
    }
    return this.responsibilityTags;
  }

  private syncTagControl(type: 'courses' | 'keywords' | 'responsibilities' | 'pains') {
    if (type === 'courses') {
      this.profileForm.get('courses_taken')?.setValue(this.tagsToString(this.courseTags), { emitEvent: false });
    } else if (type === 'keywords') {
      this.profileForm.get('objective_keywords')?.setValue(this.tagsToString(this.keywordTags), { emitEvent: false });
    } else if (type === 'pains') {
      this.profileForm.get('pains_keywords')?.setValue(this.tagsToString(this.painsTags), { emitEvent: false });
    } else {
      this.businessForm.get('responsibilities')?.setValue(this.tagsToString(this.responsibilityTags), { emitEvent: false });
    }
  }

  private parseTags(value?: string | null): string[] {
    if (!value) {
      return [];
    }
    return value
      .split(/[,;\n]/)
      .map((tag) => tag.trim())
      .filter((tag) => !!tag);
  }

  private tagsToString(tags: string[]): string {
    return tags.join(', ');
  }

  applyCpfMask(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const masked = this.formatCpf(input?.value || '');
    if (input) {
      input.value = masked;
    }
    this.profileForm.get('cpf')?.setValue(masked, { emitEvent: false });
  }

  applyPhoneMask(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const masked = this.formatPhone(input?.value || '');
    if (input) {
      input.value = masked;
    }
    this.profileForm.get('phone')?.setValue(masked, { emitEvent: false });
  }

  private stripNonDigits(value: string) {
    return (value || '').replace(/\D/g, '');
  }

  private formatCpf(value: string) {
    const digits = this.stripNonDigits(value).slice(0, 11);
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  }

  private formatPhone(value: string) {
    const digits = this.stripNonDigits(value).slice(0, 11);
    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }

  private toInputDateValue(date: string) {
    const dt = new Date(date);
    return toLocalDateTimeInputValue(dt);
  }

  progressForEnrollment(enrollment: MentoringEnrollment) {
    const total = enrollment.session_count || 0;
    if (total > 0) {
      const completed = this.completedSessions(enrollment);
      return Math.min(100, Math.round((completed / total) * 100));
    }
    return enrollment.progress_percent || 0;
  }

  completedSessions(enrollment: MentoringEnrollment) {
    return this.schedules.filter(
      (session) => session.mentoring_enrollment_id === enrollment.id && session.status === 'DONE'
    ).length;
  }

  nextSessionDate(enrollment: MentoringEnrollment) {
    const upcoming = this.schedules
      .filter(
        (session) =>
          session.mentoring_enrollment_id === enrollment.id &&
          session.status !== 'DONE' &&
          new Date(session.meeting_date).getTime() >= Date.now()
      )
      .sort((a, b) => new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime())[0];
    return upcoming ? this.formatScheduleDate(upcoming.meeting_date) : 'Sem agenda';
  }

  private syncEnrollmentFilter() {
    if (!this.programs.length) {
      this.filteredEnrollments = [];
      this.selectedEnrollment = null;
      return;
    }

    if (!this.selectedProgram) {
      this.selectedProgram = this.programs[0];
    }

    const cpfDigits = this.mentoradoCpfFilter.replace(/\D/g, '');
    this.filteredEnrollments = this.enrollments.filter((row) => {
      if (!this.showAllEnrollments) {
        const matchesProgram = row.mentoring_program_id === this.selectedProgram!.id;
        if (!matchesProgram) {
          return false;
        }
      }
      if (!cpfDigits) {
        return true;
      }
      const enrollmentCpf = row.mentorado_snapshot.cpf.replace(/\D/g, '');
      return enrollmentCpf.includes(cpfDigits);
    });

    if (this.filteredEnrollments.length) {
      const currentExists = this.selectedEnrollment
        ? this.filteredEnrollments.some((row) => row.id === this.selectedEnrollment!.id)
        : false;
      this.selectedEnrollment = currentExists ? this.selectedEnrollment : this.filteredEnrollments[0];
      if (this.selectedEnrollment) {
        this.loadMentoradoForms(this.selectedEnrollment.id);
      }
    } else {
      this.selectedEnrollment = null;
      this.profileForm.reset();
      this.businessForm.reset();
    }

    this.resolveSessionsContext();
  }

  private resolveSessionsContext() {
    this.previousTasksSub?.unsubscribe();
    this.previousFilesSub?.unsubscribe();
    this.upcomingTasksSub?.unsubscribe();
    this.currentTasksSub?.unsubscribe();
    this.previousSessionTasks = [];
    this.previousSessionFiles = [];
    this.upcomingSessionTasks = [];
    this.currentSessionTasks = [];
    this.previousSession = null;
    this.upcomingSession = null;
    this.currentSession = null;
    this.currentSessionFiles = [];
    this.currentFilesSub?.unsubscribe();

    if (!this.selectedEnrollment) {
      return;
    }

    const sessions = this.scheduleForActiveEnrollment()
      .slice()
      .sort((a, b) => new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime());
    if (!sessions.length) {
      this.editingUpcomingDate = false;
      return;
    }
    const now = new Date();
    const completed = sessions.filter((session) => session.status === 'DONE');
    this.previousSession = completed.length ? completed[completed.length - 1] : null;

    const ongoing = sessions.filter(
      (session) => session.status !== 'DONE' && new Date(session.meeting_date).getTime() <= now.getTime()
    );
    this.currentSession = ongoing.length ? ongoing[ongoing.length - 1] : null;

    const futureSessions = sessions.filter(
      (session) =>
        new Date(session.meeting_date).getTime() > now.getTime() &&
        (!this.currentSession || session.id !== this.currentSession.id) &&
        session.status !== 'DONE'
    );
    this.upcomingSession = futureSessions.length ? futureSessions[0] : null;

    if (this.previousSession) {
      this.previousTasksSub = this.mentorService
        .listTasksForSession(this.previousSession.id)
        .subscribe((tasks) => (this.previousSessionTasks = tasks));
      this.previousFilesSub = this.mentorService
        .listSessionFiles(this.previousSession.id)
        .subscribe((files) => (this.previousSessionFiles = files));
    }

    if (this.currentSession) {
      this.currentTasksSub = this.mentorService
        .listTasksForSession(this.currentSession.id)
        .subscribe((tasks) => (this.currentSessionTasks = tasks));
      this.currentFilesSub = this.mentorService
        .listSessionFiles(this.currentSession.id)
        .subscribe((files) => (this.currentSessionFiles = files));
    }

    if (this.upcomingSession) {
      this.upcomingTasksSub = this.mentorService
        .listTasksForSession(this.upcomingSession.id)
        .subscribe((tasks) => (this.upcomingSessionTasks = tasks));
    }
    this.editingUpcomingDate = false;
    this.scheduleForm.reset({ meeting_date: '' });
    if (this.currentSession) {
      this.currentSessionView = this.activeSessionId === this.currentSession.id ? 'active' : 'preview';
    } else {
      this.activeSessionId = null;
      this.currentSessionView = 'preview';
    }
    this.sessionRescheduleForm.reset({ meeting_date: '' });
    this.taskPanelCollapsed = false;
  }

  private loadMentoradoForms(enrollmentId: number) {
    this.mentorService
      .getMentoradoProfile(enrollmentId)
      .pipe(skip(1), take(1))
      .subscribe((profile: MentoringMentoradoProfile | null) => {
        if (profile) {
          this.profileForm.patchValue({
            ...profile,
            cpf: this.formatCpf(profile.cpf || ''),
            phone: this.formatPhone(profile.phone || ''),
            work_plan: profile.work_plan || ''
          });
          this.courseTags = this.parseTags(profile.courses_taken);
          this.keywordTags = this.parseTags(profile.objective_keywords);
          this.painsTags = this.parseTags(profile.pains_keywords);
        } else if (this.selectedEnrollment) {
          this.profileForm.reset({
            name: this.selectedEnrollment.mentorado_snapshot.name,
            email: this.selectedEnrollment.mentorado_snapshot.email,
            cpf: this.formatCpf(this.selectedEnrollment.mentorado_snapshot.cpf),
            phone: '',
            education_level: '',
            courses_taken: '',
            profile_description: '',
            professional_experience: '',
            extra_experience: '',
            objective: '',
            objective_keywords: '',
            pains_description: '',
            pains_keywords: '',
            work_plan: ''
          });
          this.courseTags = [];
          this.keywordTags = [];
          this.painsTags = [];
        }
        if (this.workPlanEditMode) {
          this.deferSyncWorkPlanEditor();
        }
      });

    this.mentorService
      .getMentoradoBusiness(enrollmentId)
      .pipe(skip(1), take(1))
      .subscribe((business: MentoringMentoradoBusiness | null) => {
        if (business) {
          this.businessForm.patchValue(business);
          this.responsibilityTags = this.parseTags(business.responsibilities);
        } else {
          this.businessForm.reset({
            business_sector: '',
            company_website: '',
            business_description: '',
            job_title: '',
            role_description: '',
            responsibilities: '',
            daily_activities: ''
          });
          this.responsibilityTags = [];
        }
      });
  }

  saveWorkPlan() {
    if (this.savingWorkPlan || !this.selectedEnrollment) {
      return;
    }
    this.captureWorkPlanContent();
    this.savingWorkPlan = true;
    this.saveProfile({ logWorkPlan: true })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.savingWorkPlan = false;
          this.workPlanEditMode = false;
          this.workPlanDraftBackup = this.profileForm.value.work_plan || '';
        },
        error: () => (this.savingWorkPlan = false)
      });
  }

  enterWorkPlanEdit() {
    this.workPlanDraftBackup = this.profileForm.value.work_plan || '';
    this.workPlanEditMode = true;
    this.deferSyncWorkPlanEditor();
  }

  cancelWorkPlanEdit() {
    this.profileForm.get('work_plan')?.setValue(this.workPlanDraftBackup);
    this.workPlanEditMode = false;
  }

  applyEditorCommand(command: 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList') {
    if (typeof document !== 'undefined') {
      document.execCommand(command, false);
      this.onWorkPlanInput();
    }
  }

  insertEmoji(emoji: string) {
    if (typeof document !== 'undefined') {
      document.execCommand('insertText', false, emoji);
      this.onWorkPlanInput();
    }
  }

  onWorkPlanInput() {
    this.captureWorkPlanContent();
  }

  async generateMentoringPdf() {
    if (!this.selectedEnrollment) {
      return;
    }
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const now = new Date();
    const mentorName = this.mentorNameFor(this.selectedEnrollment);
    const profile = this.profileForm.value;
    const business = this.businessForm.value;
    const finishedSessions = this.sessionHistory().filter((session) => session.status === 'DONE');
    const totalSessions = this.selectedEnrollment.session_count || 0;
    const completedSessions = finishedSessions.length;
    const progressPercent = this.progressForEnrollment(this.selectedEnrollment);
    const nextSession = this.upcomingSession?.meeting_date ? this.formatScheduleDate(this.upcomingSession.meeting_date) : 'Sem agenda';

    await this.drawPdfHeader(doc);

    let cursor = this.pdfMargin + 60;
    cursor = this.drawInfoPanel(
      doc,
      [
        { label: 'Mentorado', value: this.selectedEnrollment.mentorado_snapshot.name || '—' },
        { label: 'Mentor responsável', value: mentorName || '—' },
        { label: 'Programa', value: this.selectedProgram?.name || '—' },
        { label: 'Data do relatório', value: now.toLocaleDateString('pt-BR', { dateStyle: 'long' }) },
        { label: 'Progresso', value: `${progressPercent}%` }
      ],
      cursor
    );

    cursor = this.drawStatusPanel(doc, [
      { label: 'Status', value: this.translateEnrollmentStatus(this.selectedEnrollment.status) },
      { label: 'Sessões realizadas', value: totalSessions ? `${completedSessions}/${totalSessions}` : `${completedSessions}` },
      { label: 'Próxima sessão', value: nextSession }
    ], cursor);

    cursor = this.addSectionTitle(doc, 'Dados do mentorado', cursor);
    cursor = this.drawKeyValuePairs(
      doc,
      [
        { label: 'Nome', value: profile.name || this.selectedEnrollment.mentorado_snapshot.name || '—' },
        { label: 'Email', value: profile.email || this.selectedEnrollment.mentorado_snapshot.email || '—' },
        { label: 'CPF', value: profile.cpf || this.selectedEnrollment.mentorado_snapshot.cpf || '—' },
        { label: 'Telefone', value: profile.phone || '—' },
        { label: 'Grau de escolaridade', value: profile.education_level || '—' }
      ],
      cursor
    );
    cursor = this.drawChipGroup(doc, 'Cursos / certificações', this.courseTags, cursor);
    cursor = this.drawChipGroup(doc, 'Palavras-chave', this.keywordTags, cursor);
    cursor = this.drawParagraphBlock(doc, 'Objetivo principal', profile.objective || '—', cursor);
    cursor = this.drawParagraphBlock(doc, 'Dores do mentorado', profile.pains_description || '—', cursor);

    cursor = this.addSectionTitle(doc, 'Dados do negócio', cursor);
    cursor = this.drawKeyValuePairs(
      doc,
      [
        { label: 'Setor', value: business.business_sector || '—' },
        { label: 'Cargo', value: business.job_title || '—' },
        { label: 'Site / LinkedIn', value: business.company_website || '—' }
      ],
      cursor
    );
    cursor = this.drawParagraphBlock(doc, 'Descrição do negócio', business.business_description || '—', cursor);
    cursor = this.drawChipGroup(doc, 'Responsabilidades', this.responsibilityTags, cursor);
    cursor = this.drawParagraphBlock(doc, 'Rotina diária', business.daily_activities || '—', cursor);
    cursor = this.drawParagraphBlock(doc, 'Descrição da função', business.role_description || '—', cursor);

    if (finishedSessions.length) {
      const tasksMap = await this.loadTasksForSessions(finishedSessions);
      cursor = this.addSectionTitle(doc, 'Sessões realizadas', cursor);
      finishedSessions.forEach((session) => {
        cursor = this.drawSessionSummary(doc, session, tasksMap[session.id] || [], cursor);
      });
    }

    this.addPdfFooter(doc);
    const sanitizedName = (this.selectedEnrollment.mentorado_snapshot.name || 'mentoria').replace(/\s+/g, '-');
    doc.save(`relatorio-mentoria-${sanitizedName}.pdf`);
  }

  async downloadUpcomingSessionPdf() {
    if (!this.upcomingSession) {
      return;
    }
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    await this.drawPdfHeader(doc);
    const now = new Date();
    const mentorName = this.mentorNameFor(this.selectedEnrollment!);
    const progressPercent = this.progressForEnrollment(this.selectedEnrollment!);
    const finishedSessions = this.sessionHistory().filter((session) => session.status === 'DONE');
    const totalSessions = this.selectedEnrollment?.session_count || 0;
    const completedSessions = finishedSessions.length;

    let cursor = this.pdfMargin + 60;
    cursor = this.drawInfoPanel(
      doc,
      [
        { label: 'Mentorado', value: this.selectedEnrollment?.mentorado_snapshot.name || '—' },
        { label: 'Mentor responsável', value: mentorName || '—' },
        { label: 'Programa', value: this.selectedProgram?.name || '—' },
        { label: 'Data do relatório', value: now.toLocaleDateString('pt-BR', { dateStyle: 'long' }) },
        { label: 'Progresso', value: `${progressPercent}%` }
      ],
      cursor
    );
    cursor = this.drawStatusPanel(
      doc,
      [
        { label: 'Status', value: this.translateEnrollmentStatus(this.selectedEnrollment?.status || 'IN_PROGRESS') },
        { label: 'Sessões realizadas', value: totalSessions ? `${completedSessions}/${totalSessions}` : `${completedSessions}` },
        { label: 'Próxima sessão', value: this.formatScheduleDate(this.upcomingSession?.meeting_date) }
      ],
      cursor
    );
    const session = this.upcomingSession;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text('Próxima sessão', this.pdfMargin, cursor);
    cursor += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    this.setTextColor(doc, this.pdfTextColor);
    doc.text(`Data e horário: ${this.formatScheduleDate(session.meeting_date)}`, this.pdfMargin, cursor);
    cursor += 18;
    doc.text(`Status: ${this.sessionStatusLabel(session.status)}`, this.pdfMargin, cursor);
    cursor += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text('Tarefas planejadas', this.pdfMargin, cursor);
    cursor += 18;
    const tasks = this.upcomingSessionTasks || [];
    if (!tasks.length) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(11);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text('Nenhuma tarefa cadastrada para esta sessão.', this.pdfMargin, cursor);
      cursor += 16;
    } else {
      tasks.forEach((task, index) => {
        cursor = this.checkPageBreak(doc, cursor, 28);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        this.setTextColor(doc, this.pdfBrandDark);
        doc.text(`${index + 1}. ${task.title}`, this.pdfMargin, cursor);
        cursor += 14;
        if (task.notes) {
          doc.setFont('helvetica', 'normal');
          this.setTextColor(doc, this.pdfTextColor);
          const lines = doc.splitTextToSize(task.notes, this.pdfContentWidth(doc));
          lines.forEach((line: string) => {
            cursor = this.checkPageBreak(doc, cursor, 18);
            doc.text(line, this.pdfMargin + 14, cursor);
            cursor += 14;
          });
        }
        if (task.attachment_name) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          this.setTextColor(doc, this.pdfMutedColor);
          cursor = this.checkPageBreak(doc, cursor, 16);
          doc.text(`Anexo: ${task.attachment_name}`, this.pdfMargin + 14, cursor);
          cursor += 14;
        }
        cursor += 6;
      });
    }
    this.addPdfFooter(doc);
    doc.save(`proxima-sessao-${this.formatScheduleDate(session.meeting_date).replace(/\s+/g, '-')}.pdf`);
  }

  private captureWorkPlanContent() {
    if (this.workPlanEditor) {
      const html = this.workPlanEditor.nativeElement.innerHTML.trim();
      this.profileForm.get('work_plan')?.setValue(html);
    }
  }

  private deferSyncWorkPlanEditor() {
    setTimeout(() => {
      this.syncWorkPlanEditor();
      if (this.workPlanEditMode && this.workPlanEditor) {
        this.workPlanEditor.nativeElement.focus();
      }
    }, 0);
  }

  private syncWorkPlanEditor() {
    if (this.workPlanEditor) {
      this.workPlanEditor.nativeElement.innerHTML = this.profileForm.value.work_plan || '';
    }
  }

  private async drawPdfHeader(doc: jsPDF) {
    const width = doc.internal.pageSize.getWidth();
    doc.setFillColor(237, 250, 247);
    doc.rect(0, 0, width, 60, 'F');
    const logo = await this.ensurePdfLogo();
    if (logo) {
      doc.addImage(logo, 'PNG', this.pdfMargin, 20, 80, 20);
    }
    this.setTextColor(doc, this.pdfBrandDark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('Relatório de mentoria', this.pdfMargin + 110, 38);
  }

  private async ensurePdfLogo(): Promise<string | null> {
    if (this.pdfLogoDataUrl) {
      return this.pdfLogoDataUrl;
    }
    try {
      const response = await fetch(this.pdfLogoAssetPath);
      const blob = await response.blob();
      this.pdfLogoDataUrl = await this.blobToDataUrl(blob);
      return this.pdfLogoDataUrl;
    } catch (error) {
      console.warn('[ControlMentor] Não foi possível carregar o logo para o PDF.', error);
      return null;
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private drawInfoPanel(doc: jsPDF, entries: { label: string; value: string }[], startY: number) {
    const width = this.pdfContentWidth(doc);
    const padding = 16;
    const lineHeight = 18;
    const panelHeight = entries.length * lineHeight + padding * 2;
    const y = this.checkPageBreak(doc, startY, panelHeight + 10);
    doc.setFillColor(248, 250, 253);
    doc.roundedRect(this.pdfMargin, y, width, panelHeight, 10, 10, 'F');
    let cursor = y + padding;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    this.setTextColor(doc, this.pdfMutedColor);
    entries.forEach((entry) => {
      doc.text(entry.label.toUpperCase(), this.pdfMargin + 10, cursor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      this.setTextColor(doc, this.pdfBrandDark);
      doc.text(entry.value || '—', this.pdfMargin + 150, cursor);
      cursor += lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      this.setTextColor(doc, this.pdfMutedColor);
    });
    return y + panelHeight + 20;
  }

  private drawStatusPanel(doc: jsPDF, items: { label: string; value: string }[], startY: number) {
    const y = this.checkPageBreak(doc, startY, 70);
    const width = this.pdfContentWidth(doc);
    const column = width / items.length;
    doc.setLineWidth(1);
    doc.setDrawColor(216, 231, 226);
    doc.roundedRect(this.pdfMargin, y, width, 70, 12, 12, 'S');
    items.forEach((item, index) => {
      const x = this.pdfMargin + index * column + 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text(item.label.toUpperCase(), x, y + 22);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      this.setTextColor(doc, this.pdfBrandDark);
      const lines = doc.splitTextToSize(item.value || '—', column - 20);
      let innerY = y + 44;
      lines.forEach((line: string) => {
        doc.text(line, x, innerY);
        innerY += 16;
      });
      if (index < items.length - 1) {
        const dividerX = this.pdfMargin + (index + 1) * column;
        doc.setDrawColor(225, 235, 230);
        doc.line(dividerX, y + 10, dividerX, y + 60);
      }
    });
    return y + 90;
  }

  private addSectionTitle(doc: jsPDF, title: string, startY: number) {
    let y = this.checkPageBreak(doc, startY, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text(title, this.pdfMargin, y);
    doc.setDrawColor(204, 229, 225);
    doc.setLineWidth(1);
    doc.line(this.pdfMargin, y + 6, doc.internal.pageSize.getWidth() - this.pdfMargin, y + 6);
    return y + 20;
  }

  private drawKeyValuePairs(doc: jsPDF, entries: { label: string; value: string }[], startY: number) {
    let y = startY;
    const width = this.pdfContentWidth(doc);
    const labelWidth = 140;
    entries.forEach((entry) => {
      const estimatedHeight = 20;
      y = this.checkPageBreak(doc, y, estimatedHeight);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text(entry.label.toUpperCase(), this.pdfMargin, y);

      const lines = doc.splitTextToSize(entry.value || '—', width - labelWidth - 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfTextColor);
      doc.text(lines, this.pdfMargin + labelWidth, y);
      y += lines.length * 14 + 6;
    });
    return y + 4;
  }

  private drawChipGroup(doc: jsPDF, label: string, values: string[], startY: number) {
    let y = this.checkPageBreak(doc, startY, 36);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    this.setTextColor(doc, this.pdfMutedColor);
    doc.text(label.toUpperCase(), this.pdfMargin, y);
    y += 20;
    const chips = values.length ? values : ['—'];
    let x = this.pdfMargin;
    const contentWidth = this.pdfContentWidth(doc);
    const rowSpacing = 10;
    chips.forEach((chip) => {
      const text = chip || '—';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const chipWidth = doc.getTextWidth(text) + 18;
      if (x + chipWidth > this.pdfMargin + contentWidth) {
        y += 26 + rowSpacing;
        x = this.pdfMargin;
        y = this.checkPageBreak(doc, y, 26);
      }
      doc.setFillColor(this.pdfChipColor.r, this.pdfChipColor.g, this.pdfChipColor.b);
      doc.roundedRect(x, y - 14, chipWidth, 26, 10, 10, 'F');
      this.setTextColor(doc, this.pdfBrandDark);
      doc.text(text, x + 9, y + 2);
      x += chipWidth + 6;
    });
    return y + 24 + rowSpacing;
  }

  private drawParagraphBlock(doc: jsPDF, label: string, text: string, startY: number) {
    let y = this.checkPageBreak(doc, startY, 40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    this.setTextColor(doc, this.pdfMutedColor);
    doc.text(label.toUpperCase(), this.pdfMargin, y);
    y += 16;
    const paragraphPadding = 14;
    const paragraphWidth = this.pdfContentWidth(doc) - paragraphPadding * 2;
    const paragraphX = this.pdfMargin + paragraphPadding;
    const lines = doc.splitTextToSize(text || '—', paragraphWidth);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    this.setTextColor(doc, this.pdfTextColor);
    const baseSpaceWidth = doc.getTextWidth(' ');
    lines.forEach((line: string, index: number) => {
      y = this.checkPageBreak(doc, y, 18);
      const words = line.trim().split(/\s+/);
      if (index < lines.length - 1 && words.length > 1) {
        const gaps = words.length - 1;
        const wordsWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
        const totalGapWidth = gaps * baseSpaceWidth;
        const remaining = paragraphWidth - (wordsWidth + totalGapWidth);
        const extraPerGap = gaps > 0 ? Math.max(remaining / gaps, 0) : 0;
        let cursorX = paragraphX;
        words.forEach((word, wordIndex) => {
          doc.text(word, cursorX, y);
          if (wordIndex < words.length - 1) {
            cursorX += doc.getTextWidth(word) + baseSpaceWidth + extraPerGap;
          }
        });
      } else {
        doc.text(line, paragraphX, y);
      }
      y += 16;
    });
    return y + 8;
  }

  private async loadTasksForSessions(sessions: MentoringSchedule[]): Promise<Record<number, MentoringSessionTask[]>> {
    const result: Record<number, MentoringSessionTask[]> = {};
    await Promise.all(
      sessions.map(async (session) => {
        try {
          const tasks = await firstValueFrom(
            this.mentorService.listTasksForSession(session.id).pipe(skip(1), take(1))
          );
          result[session.id] = tasks;
        } catch {
          result[session.id] = [];
        }
      })
    );
    return result;
  }

  private drawSessionSummary(
    doc: jsPDF,
    session: MentoringSchedule,
    tasks: MentoringSessionTask[],
    startY: number
  ) {
    let y = this.checkPageBreak(doc, startY, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text(`Sessão #${this.sessionNumber(session)} · ${this.formatScheduleDate(session.meeting_date)}`, this.pdfMargin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    this.setTextColor(doc, this.pdfMutedColor);
    doc.text(`Status: ${this.sessionStatusLabel(session.status)} · Registrada em ${this.formatTimelineDate(session.created_at)}`, this.pdfMargin, y);
    const completedTasks = this.completedTaskCount(tasks);
    const completionPercent = this.taskCompletionPercent(tasks);
    y += 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text('Tarefas da sessão', this.pdfMargin, y);
    y += 14;
    if (tasks.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfTextColor);
      doc.text(`${completedTasks}/${tasks.length} concluídas (${completionPercent}%)`, this.pdfMargin, y);
      y += 14;
      tasks.forEach((task) => {
        y = this.checkPageBreak(doc, y, 18);
        const prefix = task.status === 'DONE' ? '✔' : '○';
        doc.setFont('helvetica', 'normal');
        this.setTextColor(doc, this.pdfTextColor);
        doc.text(`${prefix} ${task.title}`, this.pdfMargin, y);
        y += 14;
        if (task.notes) {
          doc.setFont('helvetica', 'italic');
          this.setTextColor(doc, this.pdfMutedColor);
          const noteLines = doc.splitTextToSize(task.notes, this.pdfContentWidth(doc) - 24);
          noteLines.forEach((line: string) => {
            y = this.checkPageBreak(doc, y, 16);
            doc.text(line, this.pdfMargin + 24, y);
            y += 14;
          });
        }
      });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text('Nenhuma tarefa registrada para esta sessão.', this.pdfMargin, y);
      y += 14;
    }
    const notes = this.notesForSession(session);
    if (notes.length) {
      y += 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfBrandDark);
      doc.text('Observações', this.pdfMargin, y);
      y += 14;
      doc.setFont('helvetica', 'normal');
      this.setTextColor(doc, this.pdfTextColor);
      notes.forEach((note) => {
        const lines = doc.splitTextToSize(`• ${note}`, this.pdfContentWidth(doc));
        lines.forEach((line: string) => {
          y = this.checkPageBreak(doc, y, 18);
          doc.text(line, this.pdfMargin, y);
          y += 16;
        });
      });
    } else {
      y += 14;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text('Nenhuma observação registrada para esta sessão.', this.pdfMargin, y);
      y += 10;
    }
    return y + 12;
  }

  private drawTimelineEntry(doc: jsPDF, title: string, description: string, startY: number) {
    let y = this.checkPageBreak(doc, startY, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    this.setTextColor(doc, this.pdfBrandDark);
    doc.text(title, this.pdfMargin, y);
    if (description) {
      y += 14;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      this.setTextColor(doc, this.pdfTextColor);
      const lines = doc.splitTextToSize(description, this.pdfContentWidth(doc));
      lines.forEach((line: string) => {
        y = this.checkPageBreak(doc, y, 16);
        doc.text(line, this.pdfMargin, y);
        y += 14;
      });
    }
    return y + 8;
  }

  private addPdfFooter(doc: jsPDF) {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.getHeight() - 24;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      this.setTextColor(doc, this.pdfMutedColor);
      doc.text(`ControlPS · Confidencial · Página ${i}/${pageCount}`, this.pdfMargin, footerY);
      const program = this.selectedProgram?.name || '';
      if (program) {
        doc.text(program, doc.internal.pageSize.getWidth() - this.pdfMargin, footerY, { align: 'right' });
      }
    }
  }

  private pdfContentWidth(doc: jsPDF) {
    return doc.internal.pageSize.getWidth() - this.pdfMargin * 2;
  }

  private checkPageBreak(doc: jsPDF, cursorY: number, blockHeight = 0) {
    const limit = doc.internal.pageSize.getHeight() - this.pdfMargin - 40;
    if (cursorY + blockHeight > limit) {
      doc.addPage();
      return this.pdfMargin + 10;
    }
    return cursorY;
  }

  private setTextColor(doc: jsPDF, color: { r: number; g: number; b: number }) {
    doc.setTextColor(color.r, color.g, color.b);
  }

}
